import { traceCameraRemove } from '../remove-trace.js';
import { useAuth0 } from '#~/auth/index.js';
import * as Commands from '#~/device/commands/index.js';
import type { DeviceHandler } from '#~/device/index.js';
import type { Named } from '#~/models/index.js';
import { raiseError } from '#~/notifications/index.js';
import { DeleteMessage } from './prompts.jsx';
import type { UseRemoveOptions } from './types.js';
import { usePrompt } from '@studio/design-system/prompt';
import type { Device } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import { createElement } from 'react';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import type { Device as DeviceId } from '#~/device/index.js';
import { useResetSelectedDevice } from '#~/room/hooks.js';
import { locateRoom } from '#~/room/utils.js';
import { resolveLinkingSurface } from '#~/analytics/domains/linking-surface.js';
import { PostHogManager } from '#~/posthog/index.js';
import { Sel, STag } from '#~/stag/index.js';
import { CurrentUser } from '#~/auth/synchronizer.js';
import { removeTransitoryCamera } from '#~/device/commands/pending.js';
import { getTransitoryDevice } from '#~/device/transitory.js';

/**
 * Hook that returns a function to imperatively remove a device.
 *
 * @param options - Prompt options.
 * @returns Imperative function.
 */
export const useRemove = (
  options: UseRemoveOptions
): ((device: Named<Device>, callback?: DeviceHandler) => void) => {
  const prompt = usePrompt();
  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();
  const resetSelectedDevice = useResetSelectedDevice(options.type);

  return useLatestFn(async (device: Named<Device>, callback?: DeviceHandler) => {
    const { type } = options;

    prompt.confirm({
      message: createElement(DeleteMessage, { device }),
      okay: {
        label: 'Delete Source',
        variant: 'danger',
        ...STag(Sel.VideoSettings, 'confirm-delete-source')
      },
      onConfirm: (answer: boolean) => {
        if (!answer) {
          return;
        }

        const { id } = device;
        traceCameraRemove('ui.confirmed', { deviceId: id });
        // Resolve the room BEFORE the removal: the delete is optimistic, so the device is gone
        // from the depot by the time the promise settles. Needed because the Advanced Settings
        // sheet renders in the app-root dialog portal, outside the room provider.
        const roomId = locateRoom(id)?.id ?? options.roomId;
        const deviceContext = resolveDeviceAnalyticsContext(id as DeviceId['id']) ?? {
          device_id: id
        };

        // A pending camera has no agent-side device: drop its tile and the
        // record its bridge persists (only that deletion needs a cloud token).
        const removeDevice = async (): Promise<void> => {
          if (getTransitoryDevice(id)) {
            await removeTransitoryCamera(id, () => getAccessTokenSilently());
            return;
          }
          traceCameraRemove('ui.token-start', { deviceId: id });
          const token = await getAccessTokenSilently();
          traceCameraRemove('ui.token-ready', { deviceId: id });
          await Commands.remove(id, token);
        };

        removeDevice()
          .then(() => {
            traceCameraRemove('ui.complete', { deviceId: id });
            // Per rule: absent-data-gates-permissively (ai.knowledge/global) — a transient absence
            // must not move the selection, so the confirmed delete has to. No-ops unless this
            // device is the stored one.
            resetSelectedDevice(id as DeviceId['id'], roomId);

            track(EVENT_NAMES.DEVICE_DELETE, EVENT_CATEGORIES.DEVICE, {
              deviceId: id,
              deviceName: device?.name,
              ...deviceContext
            });
          })
          .catch((error) => {
            // The panel is already closed; the toast is the only place a failed
            // remove or snapshot save can surface.
            traceCameraRemove('ui.failed', { deviceId: id });
            raiseError(error, { actionable: true });
          });

        // Close the caller's surface (the settings panel) on confirmed intent,
        // not after remove/save settle: a slow or hung removal used to leave the
        // panel of a deleted source open with its buttons live. The removal is
        // already started above, so closing cannot cancel or race it.
        if (callback) {
          callback(id);
        }
      },
      title: `Delete ${type} source`
    });
  });
};

/**
 * Hook that returns a function to imperatively update a device.
 *
 * @returns Imperative function.
 */
export const useUpdate = (): Commands.Update =>
  useLatestFn(async (...args: Parameters<Commands.Update>) => {
    // console.log("$tourx useUpdate", args);
    await Commands.update(...args).catch((error) => raiseError(error, { actionable: true }));
  });

/**
 * Returns a callback to invoke after a device is successfully persisted.
 *
 * Per ENG-3796 / rules ai-code-requires-tests, code-comments-required,
 * reference-rules-in-comments: fires device_add with PII-safe context from
 * resolveDeviceAnalyticsContext() and records first_camera_added_at via
 * $set_once for the onboarding wizard activation signal.
 */
export const useOnDeviceAdded = (): ((deviceId: DeviceId['id']) => void) => {
  const { track } = useAnalytics();

  return useLatestFn((deviceId: DeviceId['id']) => {
    // Falls back to bare device_id when depot hasn't populated the record yet.
    const deviceContext = resolveDeviceAnalyticsContext(deviceId) ?? { device_id: deviceId };

    // Per ENG-3796: emit device_add with the full PII-safe context helper.
    //
    // is_embedded and linking_surface are REQUIRED on device_add's canonical-event-map row,
    // and neither comes from resolveDeviceAnalyticsContext: it only reports is_embedded when
    // it can locate the device's proxy, and it never reports a surface at all. They used to be
    // applied by trackDeviceAddSuccess, which this hook now emits instead of (its callers pass
    // skipDeviceAddEvent: true), so without them here the canonical activation event ships
    // missing two of its own required properties — which is what turned
    // e2e/tests/posthog-device-add-canonical.spec.ts red on mac and windows alike.
    //
    // Defaults, not overwrites: they sit BEFORE the spreads so a located embedded device still
    // reports is_embedded: true. And is_embedded is false rather than absent because absent and
    // false read the same downstream, which silently drops every generic add from a breakdown.
    track(EVENT_NAMES.DEVICE_ADD, EVENT_CATEGORIES.DEVICE, {
      is_embedded: false,
      linking_surface: resolveLinkingSurface(),
      ...deviceContext,
      device_id: deviceId
    });

    // Per ENG-3796: $set_once is idempotent — PostHog will only write
    // first_camera_added_at on the very first camera add, never on subsequent ones.
    // posthog-js identify signature: identify(distinctId, $set, $set_once) — the
    // third argument is the $set_once bag, not a nested key in the second argument.
    const userId = PostHogManager.getInstance().getIdentifiedUser();
    // ENG-4319 requires the signed-in email on EVERY person-property write, not just
    // the one at sign-in: posthog-identify-person-email.spec.ts walks every write and
    // fails any whose $set omits it, because a write without it forks the person and
    // strands the address-free half of the journey. This call previously passed an
    // empty $set, which only stayed hidden while the add itself was failing upstream.
    // Read from CurrentUser rather than a hook so the write cannot depend on render
    // order — it is the same module the analytics context resolver reads.
    const email = CurrentUser.user?.email;

    // No email means no signed-in user, which also means no add could have happened —
    // so skipping is the honest branch. Emitting the write anyway would satisfy the
    // activation signal by breaking the identity contract that makes it queryable.
    if (userId && email) {
      PostHogManager.getInstance().identify(
        userId,
        { email },
        {
          first_camera_added_at: new Date().toISOString()
        }
      );
    }
  });
};
